import * as _ from 'lodash';
import * as React from 'react';

import * as Models from '../Models';
import { selectLink, linkToRelativeUrl } from './links';
import { getItemGroupColumnClass } from './table';
import { OutotecIcons } from '../Components/General/Icons';
import { CHEVRON_DOWN, CHEVRON_RIGHT, REMOVE, INFO } from '../Constants';
import {Row} from 'react-table'


export const getHeaderTitles: Function = (headers: string[], rows: Models.AttributeRow[]) => {
    let headerTitles: any[] = headers.map((h: any) => {
        return {
            headerName: h,
            headerAccessor: h.replace(/ /g, '_').toLowerCase()
        }
    });

    return headerTitles;
}


export const getData: Function = (headers: string[], rows: Models.GroupedAttributeRow[]) => {
    return _.map(rows, (r) => {
        const items: Models.AttributeCell[] = r.items;
        const row: any = {};

        _.forEach(getHeaderTitles(headers, rows), (h: { headerName: string, headerAccessor: string }, i: number) => {
            row[h.headerAccessor] = items[i];
        });

        if (r.children) {
            row.children = r.children;
        }

        return row;
    });
}

export const getItemGroupData: Function = (headers: string[],
    rows: Models.AttributeRow[],
    visibleColumns?: string[]): Models.DataGridContent => {
    const result: Models.DataGridContent = {
        columns: [],
        data: []
    }

    const headerTitles: any[] = getHeaderTitles(headers, rows);

    const customFilterMethod = (rows, columnId, filterValue) => {
        return rows.filter(row => {
          const col = row.values[columnId]; // Get the value of the column
          let colDescription = '';
      
          if (col?.description && Array.isArray(col.description)) {
            colDescription = col.description.length > 0 ? col.description[0] : '';
          } else {
            colDescription = col?.description || col?.url?.description || '';
          }
      
          // If there's no description or it's null, skip this row
          if (colDescription === null || colDescription === '') {
            return false;
          }
      
          // Return rows that match the filter value (case-insensitive)
          return colDescription.toLowerCase().includes(filterValue.toLowerCase());
        });
      };

      const sortByDescription = (rowA, rowB, columnId, desc) => {
        const a = rowA.values[columnId];
        const b = rowB.values[columnId];
      
        // Get description from the row data
        const descriptionA = a?.description ? a.description : a?.url?.description || '';
        const descriptionB = b?.description ? b.description : b?.url?.description || '';
      
        // If either is null, return 0
        if (descriptionA === null || descriptionB === null) {
          return 0;
        }
      
        // If both descriptions are numbers, sort numerically
        if (_.isNumber(descriptionA) && _.isNumber(descriptionB)) {
          return descriptionA - descriptionB;
        }
      
        // Otherwise, sort alphabetically using localeCompare
        return descriptionA.toString().localeCompare(descriptionB.toString());
      };

    result.columns = headerTitles.map((h: { headerName: string, headerAccessor: string }) => {
        return {
            Header: h.headerName,
            accessor: h.headerAccessor,
            Filter: ({ column }) => (
                <input
                  type='text'
                  value={column.filterValue || ''}
                  onChange={e => column.setFilter(e.target.value)}
                  placeholder="Filter..."
                  className='form-control'
                />
              ),
              filter: customFilterMethod,
              sortType:sortByDescription,
            Cell: (col: any) => {
                const attributeCell: Models.AttributeCell = col.value;

                return !attributeCell ? '' : (
                    <span title={attributeCell.toolTip || attributeCell.description}>
                        {attributeCell.url && attributeCell.url.id &&
                            <a href={linkToRelativeUrl(attributeCell.url)} onClick={(e) => { selectLink(e, attributeCell.url) }}>
                                {attributeCell.url.description}
                            </a>
                        }
                        {attributeCell.description}
                    </span>
                )
            },
            getProps: (state, rowInfo, column) => {
                const className: string = getItemGroupColumnClass(rows, rowInfo, column);
                return { className }
            }
        }
    })

    const docName: number = result.columns.findIndex(c => c.Header === 'Product Group');
    if (docName > -1) {
        result.columns[docName].style = { textTransform: 'uppercase' };
    }

    if (visibleColumns) {
        result.columns.forEach(column => {
            if (visibleColumns.indexOf(column.Header) < 0) {
                column.show = false;
            }
        });
    }

    result.data = getData(headers, rows);

    return result;
}

export const createChildRows: Function = (originalRows: Models.AttributeRow[]) => {

    var rows: Models.GroupedAttributeRow[] = [];

    var groups: _.Dictionary<Models.AttributeRow[]> = _.groupBy(originalRows, (r: Models.AttributeRow) => {

        if (r.items.length < 5 || !r.items[2].url.description) {
            return 'unknown';
        }

        return `${r.items[2].url.description}`;
    });

    Object.keys(groups).forEach((key: string) => {

        let group: Models.GroupedAttributeRow[] = groups[key];
        if (key !== 'unknown' && group.length > 1 && group[0].items.length >= 2) {
            // Duplicates, create header row to group
            let header: Models.GroupedAttributeRow = _.cloneDeep(group[0]);
            let members: Models.GroupedAttributeRow[] = _.cloneDeep(group);

            if (header.items.length >=5) {
                let assetCount: number = _.uniq(members.map(row => row.items[4].url.id)).filter(v => v).length;
                members = _.uniqBy(members, (row) => row.items[3].url.id).filter(r => r.items[3].url.id);

                header.items[3].description = members.length.toString();
                header.items[3].url = {} as Models.Link;
                header.items[4].description = assetCount.toString();
                header.items[4].url = {} as Models.Link;
            }

            header.children = [...members];
            rows.push(header);
        } else {
            group.forEach(row => {
                if (row.items.length >=5) {
                    row.items[3].description = '0';
                    row.items[3].url = {} as Models.Link;
                    row.items[4].description = '1';
                    row.items[4].url = {} as Models.Link;
                }
            });
            rows.push(...group);
        }
     });

    return rows;
}

export const withExpander: (
    content: Models.DataGridContent,
) => Models.DataGridContent = (content) => {
    const expander: any = {
        id:'expander',
        width: 20,
        maxWidth:20,
        Header: '',
            Cell: ({ row}:{row:Row}) => {
            if (row.original.children && row.original.children.length > 0) {
                return (
                    <span {...row.getToggleRowExpandedProps()}>
                        {row.isExpanded && <OutotecIcons type={CHEVRON_DOWN} />}
                        {!row.isExpanded && <OutotecIcons type={CHEVRON_RIGHT} />}
                    </span>
                );
            }
            return <div />;
        },
        getProps: (state, rowInfo, column) => {
            if (!rowInfo.original.children || !rowInfo.original.children.length) {
                return { 'data-childrowcount': 0 }
            }
            return { 'data-childrowcount': rowInfo.original.children.length }
        },
        style: {
            paddingLeft: '5px'
        }
    };

    return { ...content, columns: [expander].concat(content.columns) };
};
